import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const SUPPORTED_LOCALES = ['en-US', 'en-IN'];
const FALLBACK_LOCALE = 'en-US';
const STORAGE_KEY = 'ih_locale';

const LocaleContext = createContext({
  locale: FALLBACK_LOCALE,
  setLocale: () => {},
  supportedLocales: SUPPORTED_LOCALES,
});

function resolveBrowserLocale() {
  const candidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  const normalized = candidates.map((value) => String(value || '').trim()).filter(Boolean);
  if (normalized.includes('en-IN')) return 'en-IN';
  if (normalized.includes('en-US')) return 'en-US';
  const englishCandidate = normalized.find((value) => value.toLowerCase().startsWith('en'));
  if (englishCandidate && englishCandidate.toLowerCase().includes('in')) return 'en-IN';
  return FALLBACK_LOCALE;
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED_LOCALES.includes(stored)) return stored;
    return resolveBrowserLocale();
  });

  const setLocale = (nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return;
    setLocaleState(nextLocale);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES,
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
